import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { ExternalLink, Link as LinkIcon, Edit3, Eye } from "lucide-react";
import type { ProfilePage } from "../api/pages";
import { navigate } from "../lib/router";
import { useLanguage } from "../hooks/useLanguage";

type PageCardProps = {
  page: ProfilePage;
  onEdit: () => void;
};

export function PageCard({ page, onEdit }: PageCardProps) {
  const { t } = useLanguage();
  const displayTitle = page.title || t.pageCard.untitled;
  const initials = (displayTitle[0] || page.slug[0] || "L").toUpperCase();
  const publicUrl =
    typeof window === "undefined" ? `/${page.slug}` : `${window.location.origin}/${page.slug}`;

  const copyToClipboard = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(publicUrl);
    // Could add a toast here
  };

  return (
    <Card className="group relative flex h-full flex-col gap-4 overflow-hidden border-2 border-slate-100 bg-white p-6 transition-all duration-300 hover:border-brand-primary/20 hover:shadow-xl hover:shadow-brand-primary/10 hover:-translate-y-2">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#0DAFFD] to-[#0FD8FE] text-lg font-bold text-white shadow-lg shadow-[#0DAFFD]/20">
            {initials}
          </div>
          <div className="min-w-0">
            <h3 className="truncate text-lg font-bold text-slate-900">{displayTitle}</h3>
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <LinkIcon className="h-3.5 w-3.5" />
              <span className="truncate max-w-[120px]">/{page.slug}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-auto space-y-4">
        <div className="flex items-center justify-between border-t border-slate-100 pt-4">
          <div className="flex items-center gap-1.5 text-sm text-slate-600">
            <Eye className="h-4 w-4 text-brand-primary" />
            <span className="font-bold text-slate-900">{page.today_accesses || 0}</span>
            <span className="text-slate-500">{t.pageCard.accessesToday}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="ghost"
            onClick={() => navigate(`/app/detalhes/${page.slug}`)}
            className="w-full justify-center whitespace-nowrap"
            icon={<Eye className="h-4 w-4" />}
          >
            {t.pageCard.viewDetails}
          </Button>
          <Button
            variant="primary"
            onClick={() => window.open(publicUrl, '_blank')}
            icon={<ExternalLink className="h-4 w-4" />}
          >
            {t.pageCard.visit}
          </Button>
        </div>
      </div>
    </Card>
  );
}
